/*
  Meta CAPI helper for web (Pixel + Server) deduplication.
  - Collects fbp/fbc from cookies (and creates _fbc from fbclid when present)
  - Generates event_id
  - Sends payload to backend endpoint: /meta-capi-web/event/send
  - Calls fbq('track', ...) with eventID for dedup with browser Pixel
*/

import ReactPixel from "react-facebook-pixel";
import { v4 as uuidv4 } from "uuid";
import { axiosInstance } from "api/instance";

const FBP_COOKIE = "_fbp";
const FBC_COOKIE = "_fbc";

function getCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(name + "="));
  if (!match) return undefined;
  try {
    const raw = decodeURIComponent(match.split("=")[1]);
    // Normalize empty/invalid persisted values
    if (!raw || raw === "null" || raw === "undefined") return undefined;
    return raw;
  } catch {
    const raw = match.split("=")[1];
    if (!raw || raw === "null" || raw === "undefined") return undefined;
    return raw;
  }
}

function setCookie(name: string, value: string, days = 90) {
  if (typeof document === "undefined") return;
  const expires = new Date();
  expires.setDate(expires.getDate() + days);
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; expires=${expires.toUTCString()}; SameSite=Lax`;
}

function getQueryParam(param: string): string | undefined {
  if (typeof window === "undefined") return undefined;
  const url = new URL(window.location.href);
  const v = url.searchParams.get(param);
  return v ?? undefined;
}

/**
 * Ensure _fbc cookie exists using fbclid from URL when available.
 * Standard value format: fb.1.<timestamp_ms>.<fbclid>
 */
export function ensureFbcFromUrl(): string | undefined {
  const currentFbc = getCookie(FBC_COOKIE);
  // Validate existing _fbc format: fb.1.<timestamp>.<id>
  const fbcPattern = /^fb\.1\.\d+\..+/;
  if (currentFbc && fbcPattern.test(currentFbc)) return currentFbc;

  // Some links may carry fbc directly
  const fbcFromQuery = getQueryParam("fbc");
  if (fbcFromQuery && fbcPattern.test(fbcFromQuery)) {
    setCookie(FBC_COOKIE, fbcFromQuery);
    return fbcFromQuery;
  }

  const fbclid = getQueryParam("fbclid");
  if (!fbclid) return undefined;
  const ts = Date.now();
  const fbc = `fb.1.${ts}.${fbclid}`;
  setCookie(FBC_COOKIE, fbc);
  return fbc;
}

export function getFbp(): string | undefined {
  return getCookie(FBP_COOKIE);
}

export function getFbc(): string | undefined {
  return getCookie(FBC_COOKIE);
}

export interface TrackOptions {
  eventId?: string;
  eventSourceUrl?: string;
}

export interface CapiPayload {
  eventName: string;
  eventTime: number; // Unix timestamp (sec)
  eventId: string;
  eventSourceUrl?: string;
  userData: {
    clientUserAgent?: string;
    fbp?: string;
    fbc?: string;
  } & Record<string, unknown>;
  customData?: Record<string, unknown>;
}

export async function trackCapiStandardEvent(
  eventName: string,
  eventParams?: Record<string, unknown>,
  options?: TrackOptions
): Promise<{ eventId: string } | void> {
  try {
    ensureFbcFromUrl();

    const eventId = options?.eventId ?? uuidv4();
    const fbp = getFbp();
    const fbc = getFbc();
    const userAgent =
      typeof navigator !== "undefined" ? navigator.userAgent : undefined;
    const eventSourceUrl =
      options?.eventSourceUrl ??
      (typeof window !== "undefined" ? window.location.href : undefined);

    // Call Pixel with eventID for deduplication
    try {
      ReactPixel.fbq("track", eventName, eventParams || {}, {
        eventID: eventId,
      });
    } catch (e) {
      // fallback to standard track (without eventID) if fbq call fails
      // ReactPixel.track(eventName, eventParams || {});
    }

    // Only include defined userData fields
    const userData: CapiPayload["userData"] = {
      clientUserAgent: userAgent,
      ...(fbp ? { fbp } : {}),
      ...(fbc ? { fbc } : {}),
    };

    const payload: CapiPayload = {
      eventName: eventName,
      eventTime: Math.floor(Date.now() / 1000),
      eventId: eventId,
      eventSourceUrl: eventSourceUrl,
      userData,
      customData: eventParams || {},
    };

    // Fire-and-forget; do not block user interactions
    axiosInstance
      .post("/ad/meta-capi-web/event/send", payload)
      .catch(() => void 0);

    return { eventId };
  } catch (err) {
    // Best-effort: still try to send pixel event even if CAPI prep fails
    try {
      // ReactPixel.track(eventName, eventParams || {});
    } catch (_) {
      // ignore
    }
  }
}
